const london = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London",
  calendar: "gregory",
  numberingSystem: "latn",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

type LocalDateTime = Readonly<{
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}>;

function parts(epoch: number): LocalDateTime {
  const values = london.formatToParts(epoch);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(values.find((part) => part.type === type)?.value);
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

function calendarEpoch(value: LocalDateTime): number {
  return Date.UTC(value.year, value.month - 1, value.day, value.hour, value.minute, value.second);
}

function same(a: LocalDateTime, b: LocalDateTime): boolean {
  return (
    a.year === b.year &&
    a.month === b.month &&
    a.day === b.day &&
    a.hour === b.hour &&
    a.minute === b.minute &&
    a.second === b.second
  );
}

/** Resolve London wall time; a fold chooses the later instant and a gap shifts forward. */
function resolve(value: LocalDateTime): number {
  const desired = calendarEpoch(value);
  const offsets = new Set<number>();
  for (let hours = -48; hours <= 48; hours += 6) {
    const probe = desired + hours * 3_600_000;
    offsets.add(calendarEpoch(parts(probe)) - probe);
  }
  const exact = [...offsets]
    .map((offset) => desired - offset)
    .filter((candidate) => same(parts(candidate), value));
  if (exact.length > 0) return Math.max(...exact);
  for (let minute = 1; minute <= 180; minute += 1) {
    const shifted = new Date(desired + minute * 60_000);
    const candidateValue = {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      hour: shifted.getUTCHours(),
      minute: shifted.getUTCMinutes(),
      second: shifted.getUTCSeconds(),
    };
    const candidates = [...offsets]
      .map((offset) => calendarEpoch(candidateValue) - offset)
      .filter((candidate) => same(parts(candidate), candidateValue));
    if (candidates.length > 0) return Math.min(...candidates);
  }
  throw new RangeError("The London calendar is unavailable.");
}

function shiftedDate(source: LocalDateTime, days: number): LocalDateTime {
  const date = new Date(Date.UTC(source.year, source.month - 1, source.day));
  date.setUTCDate(date.getUTCDate() + days);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: source.hour,
    minute: source.minute,
    second: source.second,
  };
}

/** Fourteen calendar days at the same Europe/London wall time, not 336 elapsed hours. */
export function calculateSupportCloseDue(resolvedAtEpochMilliseconds: number): number {
  if (!Number.isSafeInteger(resolvedAtEpochMilliseconds) || resolvedAtEpochMilliseconds < 0)
    throw new RangeError("The resolution time is unavailable.");
  return resolve(shiftedDate(parts(resolvedAtEpochMilliseconds), 14));
}

/** Twelve calendar months, clamping an invalid day to month end. */
export function calculateSupportExpiry(closedAtEpochMilliseconds: number): number {
  if (!Number.isSafeInteger(closedAtEpochMilliseconds) || closedAtEpochMilliseconds < 0)
    throw new RangeError("The closure time is unavailable.");
  const source = parts(closedAtEpochMilliseconds);
  const targetYear = source.year + 1;
  const finalDay = new Date(Date.UTC(targetYear, source.month, 0)).getUTCDate();
  return resolve({ ...source, year: targetYear, day: Math.min(source.day, finalDay) });
}

/** Last complete Monday-to-Monday reporting interval in the London calendar. */
export function calculateLastCompleteSupportWeek(nowEpochMilliseconds: number): Readonly<{
  fromEpochMilliseconds: number;
  toEpochMilliseconds: number;
  calendarVersion: "support-week-london-v1";
}> {
  if (!Number.isSafeInteger(nowEpochMilliseconds) || nowEpochMilliseconds < 0)
    throw new RangeError("The reporting time is unavailable.");
  const current = parts(nowEpochMilliseconds);
  const weekday = new Date(Date.UTC(current.year, current.month - 1, current.day)).getUTCDay();
  const daysSinceMonday = (weekday + 6) % 7;
  const currentMonday = shiftedDate(
    { ...current, hour: 0, minute: 0, second: 0 },
    -daysSinceMonday,
  );
  return {
    fromEpochMilliseconds: resolve(shiftedDate(currentMonday, -7)),
    toEpochMilliseconds: resolve(currentMonday),
    calendarVersion: "support-week-london-v1",
  };
}
